// queryClient.ts

import { QueryClient, defaultShouldDehydrateQuery, isServer } from '@tanstack/react-query';
import { cache } from 'react';

const STALE_TIME = 3600; // 1 hour
const GC_TIME = 21600; // 6 hours

const makeQueryClient = () => {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // default stale time on all queries
        // amount of time data is considered fresh, after which it is refetched automatically
        staleTime: STALE_TIME,
        // default gc time on all queries
        // amount of time unused data is kept in the cache before its thrown out
        gcTime: GC_TIME,
        // Don't refetch on mount
        refetchOnMount: false,
        // Don't refetch on window focus
        refetchOnWindowFocus: false,
        // Don't refetch on reconnect
        refetchOnReconnect: false
      },
      dehydrate: {
        // include pending queries in dehydration
        shouldDehydrateQuery: (query) =>
          defaultShouldDehydrateQuery(query) || query.state.status === 'pending',
        shouldRedactErrors: () => {
          // We should not catch Next.js server errors
          // as that's how Next.js detects dynamic pages
          // so we cannot redact them.
          // Next.js also automatically redacts errors for us
          // with better digests.
          return false;
        }
      }
    }
  });
};

let browserQueryClient: QueryClient | undefined = undefined;

const getQueryClient = () => {
  if (isServer) {
    // Server: always make a new query client
    return cache(makeQueryClient)();
  } else {
    // Browser: make a new query client if we don't already have one
    // This is very important, so we don't re-make a new client if React
    // suspends during the initial render. This may not be needed if we
    // have a suspense boundary BELOW the creation of the query client
    if (!browserQueryClient) {
      browserQueryClient = makeQueryClient();
    }
    return browserQueryClient;
  }
};

export default getQueryClient;
